<template>
  <div class="signal-processing-chart relative">
    <div v-if="showControls" class="mb-2 flex items-center relative">
      <!-- Checkbox on far left -->
      <label class="flex items-center text-sm text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          v-model="localHideRawLowQualityWithImputation"
          class="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>{{ localHideRawLowQualityWithImputation ? 'Hiding' : 'Hide' }} raw low-quality data with imputations</span>
      </label>
      
      <!-- Centered Title (only show if provided) -->
      <div v-if="headerTitle" class="absolute left-1/2 transform -translate-x-1/2">
        <h2 class="text-lg font-semibold text-gray-900">{{ headerTitle }}</h2>
      </div>
      
      <!-- Download Button on far right -->
      <div class="ml-auto">
        <ChartDownloadButton
          :chart-instance="chartInstance"
          chart-name="signal-processing-chart"
          :is-chart-ready="isChartReady"
        />
      </div>
    </div>
    <div class="relative">
      <BaseChart
        ref="baseChart"
        chart-type="line"
        :chart-data="processedChartData"
        :chart-options="chartOptions"
        :width="width"
        :height="height"
        :auto-update="autoUpdate"
        class="transition-opacity duration-300"
        :class="{ 'opacity-50': !isDataFullyLoaded }"
        @chart-ready="onChartReady"
        @chart-updated="onChartUpdated"
        @chart-error="onChartError"
      />
      <!-- Loading overlay -->
      <div
        v-if="!isDataFullyLoaded"
        class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10"
      >
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
          <p class="text-sm text-gray-600">Loading data<span class="loading-dots"></span></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseChart from './BaseChart.vue'
import ChartDownloadButton from './ChartDownloadButton.vue'
import { Chart } from 'chart.js'
import { SIGNAL_PROCESSING_COLORS } from '../config/chartColors.js'

export default {
  name: 'SignalProcessingChart',
  components: {
    BaseChart,
    ChartDownloadButton
  },
  inject: ['globalState'],
  props: {
    // Data props
    filteredData: {
      type: Array,
      required: true
    },
    curveFeatures: {
      type: Array,
      default: () => []
    },
    selectedCurveIndex: {
      type: String,
      default: ''
    },
    hideRawLowQualityWithImputation: {
      type: Boolean,
      default: false
    },
    // Header / controls
    headerTitle: {
      type: String,
      default: ''
    },
    showControls: {
      type: Boolean,
      default: true
    },
    // Chart display props
    width: {
      type: [Number, String],
      default: 800
    },
    height: {
      type: [Number, String],
      default: 600
    },
    autoUpdate: {
      type: Boolean,
      default: true
    },
    // Timeline props
    timelineStart: {
      type: String,
      required: true
    },
    timelineEnd: {
      type: String,
      required: true
    },

    // Curve timeline mapping for timezone-aware curve positioning
    curveTimelineMap: {
      type: Object,
      default: () => ({})
    },
    // Allow charts to render even when data has missing rows/gaps
    // Useful for day-level charts where some days may have incomplete data
    allowMissingRows: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      chartInstance: null,
      isChartReady: false,
      _cachedChartOptions: null,
      _cachedProcessedData: null,
      // Local, internally controlled checkbox state
      localHideRawLowQualityWithImputation: this.hideRawLowQualityWithImputation
    }
  },
  computed: {
    // Check if data covers the entire x-axis range
    isDataFullyLoaded() {
      // If allowMissingRows is true, only check if we have any data at all
      // This allows charts to render even with gaps or incomplete coverage
      if (this.allowMissingRows) {
        return this.filteredData && this.filteredData.length > 0
      }
      
      // If no timeline bounds are set, consider data as loaded (can't verify)
      if (!this.timelineStart || !this.timelineEnd) {
        return true
      }
      
      // If no data, consider as not loaded
      if (!this.filteredData || this.filteredData.length === 0) {
        return false
      }
      
      try {
        const timelineStartTime = new Date(this.timelineStart).getTime()
        const timelineEndTime = new Date(this.timelineEnd).getTime()
        
        // Get all timestamps from the data (even if TAC values are missing)
        const timestamps = this.filteredData
          .map(row => {
            if (row.tMs != null) {
              return row.tMs
            } else if (row.datetime) {
              const d = new Date(row.datetime)
              return isNaN(d.getTime()) ? null : d.getTime()
            }
            return null
          })
          .filter(ts => ts !== null)
          .sort((a, b) => a - b)
        
        if (timestamps.length === 0) {
          return false
        }
        
        const firstTimestamp = timestamps[0]
        const lastTimestamp = timestamps[timestamps.length - 1]
        
        // Allow a small tolerance (5 minutes) for edge cases
        const tolerance = 5 * 60 * 1000 // 5 minutes in milliseconds
        
        // Check if data covers the timeline range
        const coversStart = firstTimestamp <= timelineStartTime + tolerance
        const coversEnd = lastTimestamp >= timelineEndTime - tolerance
        
        // Also check for large gaps in the data (more than 1 hour gap might indicate missing data)
        // This is optional but helps detect if data is still loading
        let hasLargeGaps = false
        if (timestamps.length > 1) {
          for (let i = 1; i < timestamps.length; i++) {
            const gap = timestamps[i] - timestamps[i - 1]
            // If gap is larger than 2 hours, might indicate missing data
            if (gap > 2 * 60 * 60 * 1000) {
              hasLargeGaps = true
              break
            }
          }
        }
        
        // Data is fully loaded if it covers the range and doesn't have suspiciously large gaps
        const isLoaded = coversStart && coversEnd && !hasLargeGaps
        
        return isLoaded
      } catch (error) {
        console.warn('Error checking if data is fully loaded:', error)
        // On error, assume data is loaded to avoid blocking the UI
        return true
      }
    },
    
    // Process chart data for signal processing visualization
    processedChartData() {

      
      // Cache the processed data to prevent unnecessary recreation
      // Include selectedCurveIndex in cache key so curve lines update when selection changes
      const cacheKey = `${this.filteredData?.length || 0}-${this.localHideRawLowQualityWithImputation}-${this.selectedCurveIndex}-${this.timelineStart}-${this.timelineEnd}`
      if (this._cachedProcessedData && this._cachedProcessedData.key === cacheKey) {

        return this._cachedProcessedData.data
      }
      

      
      if (!this.filteredData || this.filteredData.length === 0) {

        return { datasets: [] }
      }


      

      const datasets = []
      
      // 1. Curve Threshold (line, not data points)
      // Use the threshold from the selected curve (or first curve if none selected)
      // The threshold should be curve_threshold (capped between 1-10), not unadjusted_threshold
      if (this.curveFeatures.length > 0) {
        // Determine which curve to use based on selectedCurveIndex
        const curveIndex = this.selectedCurveIndex !== '' && !isNaN(parseInt(this.selectedCurveIndex))
          ? parseInt(this.selectedCurveIndex)
          : 0
        
        const curveToUse = this.curveFeatures[curveIndex] || this.curveFeatures[0]
        let threshold = null
        
        // Priority 1: Use curve_threshold (the actual threshold used to identify the curve, capped 1-10)
        if (curveToUse.curve_threshold !== undefined && curveToUse.curve_threshold !== null) {
          threshold = parseFloat(curveToUse.curve_threshold)
        }
        // Priority 2: Fallback to threshold (if curve_threshold not available)
        else if (curveToUse.threshold !== undefined && curveToUse.threshold !== null) {
          threshold = parseFloat(curveToUse.threshold)
        }
        // Priority 3: Fallback to default (should not happen if curve_threshold is properly set)
        else {
          threshold = 10 // manual threshold fallback
        }
        
        // Ensure threshold is valid and capped at 10 (as per user requirement)
        if (threshold && !isNaN(threshold) && threshold !== 'auto') {
          threshold = Math.min(10, Math.max(1, threshold)) // Cap between 1-10
          
          const timeRange = this.filteredData.length > 0 ? {
            start: new Date(this.filteredData[0].datetime),
            end: new Date(this.filteredData[this.filteredData.length - 1].datetime)
          } : null
          
          if (timeRange) {
            datasets.push({
              label: 'Curve Threshold',
              data: [
                { x: timeRange.start, y: threshold },
                { x: timeRange.end, y: threshold }
              ],
              borderColor: 'black',
              borderDash: [5, 5],
              borderWidth: 2,
              fill: false,
              pointRadius: 0,
              showLine: true,
              type: 'line'
            })
          }
        }
      }
      
      // 2. Passed data
      const passed = this.filteredData.filter(row => row.imp_cand === 0)
      if (passed.length > 0) {
        const passedData = this.processDataPoints(passed, 'TAC_pre_imputation')
        if (passedData.length > 0) {
          datasets.push({
            label: 'Passed',
            data: passedData,
            backgroundColor: 'darkblue',
            pointStyle: '.',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 3. Gap data (choose membership column based on checkbox)
      const gap = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.gap_without_imputation !== undefined) {
          return row.gap_without_imputation === 1
        }
        return row.gap === 1
      })
      if (gap.length > 0) {
        const gapData = this.processDataPoints(gap, this.getRawValueFields())
        if (gapData.length > 0) {
          datasets.push({
            label: 'Gap',
            data: gapData,
            backgroundColor: 'red',
            pointStyle: '.',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 4. Imputed Gap data
      const gapImputed = this.filteredData.filter(row => row.gap_imputed === 1)
      if (gapImputed.length > 0) {
        const gapImputedData = this.processDataPoints(gapImputed, this.getImputedValueFields())
        if (gapImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Gap',
            data: gapImputedData,
            backgroundColor: 'gray',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'black',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // 5. Non-Wear data
      const nonWear = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.non_wear_without_imputation !== undefined) {
          return row.non_wear_without_imputation === 1
        }
        return row.non_wear === 1
      })
      if (nonWear.length > 0) {
        const nonWearData = this.processDataPoints(nonWear, this.getRawValueFields())
        if (nonWearData.length > 0) {
          datasets.push({
            label: 'Non-Wear',
            data: nonWearData,
            backgroundColor: 'lightpink',
            borderColor: 'lightpink',
            pointBackgroundColor: 'lightpink',
            pointBorderColor: 'lightpink',
            pointStyle: 'cross',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 6. Imputed Non-Wear data
      const nonWearImputed = this.filteredData.filter(row => row.non_wear_imputed === 1)
      if (nonWearImputed.length > 0) {
        const nonWearImputedData = this.processDataPoints(nonWearImputed, this.getImputedValueFields())
        if (nonWearImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Non-Wear',
            data: nonWearImputedData,
            backgroundColor: 'lightpink',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'darkred',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // 7. Jump data
      const jump = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.jump_without_imputation !== undefined) {
          return row.jump_without_imputation === 1
        }
        return row.jump_imp_cand === 1
      })
      if (jump.length > 0) {
        const jumpData = this.processDataPoints(jump, this.getRawValueFields())
        if (jumpData.length > 0) {
          datasets.push({
            label: 'Jump',
            data: jumpData,
            backgroundColor: 'lightblue',
            pointStyle: 'triangle',
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 8. Imputed Jump data (matches CLI: shows whatever flags are set)
      const jumpImputed = this.filteredData.filter(row => row.jump_imputed === 1)
      if (jumpImputed.length > 0) {
        const jumpImputedData = this.processDataPoints(jumpImputed, this.getImputedValueFields())
        if (jumpImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Jump',
            data: jumpImputedData,
            backgroundColor: 'lightblue',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'blue',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // 9. Plummet data
      const plummet = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.plummet_without_imputation !== undefined) {
          return row.plummet_without_imputation === 1
        }
        return row.plummet_imp_cand === 1
      })
      if (plummet.length > 0) {
        const plummetData = this.processDataPoints(plummet, this.getRawValueFields())
        if (plummetData.length > 0) {
          datasets.push({
            label: 'Plummet',
            data: plummetData,
            backgroundColor: 'orange',
            pointStyle: 'triangle',
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 10. Imputed Plummet data (matches CLI: shows whatever flags are set)
      const plummetImputed = this.filteredData.filter(row => row.plummet_imputed === 1)
      if (plummetImputed.length > 0) {
        const plummetImputedData = this.processDataPoints(plummetImputed, this.getImputedValueFields())
        if (plummetImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Plummet',
            data: plummetImputedData,
            backgroundColor: 'orange',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'darkorange',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // 11. Extreme Negative data
      const extremeNegative = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.extreme_negative_without_imputation !== undefined) {
          return row.extreme_negative_without_imputation === 1
        }
        return row.extreme_negative_imp_cand === 1
      })
      if (extremeNegative.length > 0) {
        const extremeNegativeData = this.processDataPoints(extremeNegative, this.getRawValueFields())
        if (extremeNegativeData.length > 0) {
          datasets.push({
            label: 'Extreme Negative',
            data: extremeNegativeData,
            backgroundColor: 'lightsteelblue',
            pointStyle: '*',
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 12. Imputed Extreme Negative data
      const extremeNegativeImputed = this.filteredData.filter(row => row.extreme_negative_imputed === 1)
      if (extremeNegativeImputed.length > 0) {
        const extremeNegativeImputedData = this.processDataPoints(extremeNegativeImputed, this.getImputedValueFields())
        if (extremeNegativeImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Extreme Negative',
            data: extremeNegativeImputedData,
            backgroundColor: 'lightsteelblue',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'purple',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // 13. Proximal Low Quality data
      const proximalLowQuality = this.filteredData.filter(row => {
        if (this.localHideRawLowQualityWithImputation && row.proximal_low_quality_without_imputation !== undefined) {
          return row.proximal_low_quality_without_imputation === 1
        }
        return row.proximal_low_quality_imp_cand === 1
      })
      if (proximalLowQuality.length > 0) {
        const proximalLowQualityData = this.processDataPoints(proximalLowQuality, this.getRawValueFields())
        if (proximalLowQualityData.length > 0) {
          datasets.push({
            label: 'Proximal Low Quality',
            data: proximalLowQualityData,
            backgroundColor: 'gray',
            pointStyle: 'rect',
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: false,
            type: 'scatter'
          })
        }
      }
      
      // 14. Imputed Proximal Low Quality data
      const proximalLowQualityImputed = this.filteredData.filter(row => row.proximal_low_quality_imputed === 1)
      if (proximalLowQualityImputed.length > 0) {
        const proximalLowQualityImputedData = this.processDataPoints(proximalLowQualityImputed, this.getImputedValueFields())
        if (proximalLowQualityImputedData.length > 0) {
          datasets.push({
            label: 'Imputed Proximal Low Quality',
            data: proximalLowQualityImputedData,
            backgroundColor: 'gray',
            pointStyle: 'circle',
            pointRadius: 2,
            pointHoverRadius: 4,
            showLine: false,
            borderColor: 'darkgreen',
            borderWidth: 1,
            type: 'scatter'
          })
        }
      }
      
      // Add curve start/end lines
      if (this.curveFeatures.length > 0) {
        this.addCurveLines(datasets)
      }
      
      // Filter out empty datasets
      const validDatasets = datasets.filter(dataset => dataset.data && dataset.data.length > 0)
      
      const result = { datasets: validDatasets }
      
      // Cache the processed data
      this._cachedProcessedData = {
        key: cacheKey,
        data: result
      }
      
      return result
    },
    
    // Chart options with timeline-specific configuration
    chartOptions() {

      
      // Don't create options if timeline is not ready
      if (!this.timelineStart || !this.timelineEnd) {

        return null
      }
      
      // Calculate y-axis bounds first (they depend on data, so need to be included in cache key)
      const yAxisMin = this.getYAxisMin()
      const yAxisMax = this.getYAxisMax()
      
      // Cache the options to prevent unnecessary recreation
      // Include y-axis bounds in cache key so chart updates when data changes
      const cacheKey = `${this.timelineStart}-${this.timelineEnd}-${yAxisMin}-${yAxisMax}`
      if (this._cachedChartOptions && this._cachedChartOptions.key === cacheKey) {

        return this._cachedChartOptions.options
      }
      
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 35, // Increased padding for x-axis label and spacing
            left: 10,
            right: 10
          }
        },
        animation: {
          duration: 0
        },
        plugins: {
          tooltip: {
            enabled: true
          },
          legend: {
            position: 'top',
            onClick: (e, legendItem, legend) => {
              try {
                // Add defensive programming to prevent undefined errors
                if (!legendItem || !legend || !legend.chart) {
                  console.warn('Legend click handler: Invalid parameters received')
                  return
                }
                
                const chart = legend.chart;
                
                // Handle grouped curve start/end legend items
                if (legendItem.datasetIndex === 'curve-start' || legendItem.datasetIndex === 'curve-end') {
                  const legendText = legendItem.text || ''
                  const isStart = legendText === 'Curve Start'
                  const isEnd = legendText === 'Curve End'
                  
                  if (isStart || isEnd) {
                    chart.data.datasets.forEach((dataset, index) => {
                      if (dataset && dataset.label && typeof dataset.label === 'string' && dataset.label.includes('Curve')) {
                        const isStartDataset = dataset.label.includes('Start')
                        const isEndDataset = dataset.label.includes('End')
                        
                        if ((isStart && isStartDataset) || (isEnd && isEndDataset)) {
                          const meta = chart.getDatasetMeta(index)
                          if (meta) {
                            meta.hidden = !meta.hidden
                          }
                        }
                      }
                    })
                    chart.update()
                    return
                  }
                }
                
                // Default behavior for regular datasets
                const index = legendItem.datasetIndex
                if (typeof index === 'number' && index >= 0) {
                  const ci = chart.getDatasetMeta(index)
                  if (ci) {
                    ci.hidden = !ci.hidden
                    chart.update()
                  }
                }
              } catch (error) {
                console.warn('Error in legend onClick handler:', error)
              }
            },
            labels: {
              usePointStyle: true,
              padding: 20,
              generateLabels: (chart) => {
                // Start from Chart.js defaults so point styles are preserved correctly
                const defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart)
                const datasets = chart.data.datasets
                
                // Reorder the default labels to the desired order
                const desiredOrder = [
                  'Curve Threshold',
                  'Passed',
                  'Gap',
                  'Imputed Gap',
                  'Non-Wear',
                  'Imputed Non-Wear',
                  'Jump',
                  'Imputed Jump',
                  'Plummet',
                  'Imputed Plummet',
                  'Extreme Negative',
                  'Imputed Extreme Negative',
                  'Proximal Low Quality',
                  'Imputed Proximal Low Quality'
                ]
                
                const used = new Set()
                const ordered = []
                
                const pushWithPointStyle = (label) => {
                  const ds = datasets[label.datasetIndex]
                  if (ds && ds.pointStyle) {
                    label.pointStyle = typeof ds.pointStyle === 'function' ? ds.pointStyle() : ds.pointStyle
                  }
                  ordered.push(label)
                  used.add(label)
                }
                
                desiredOrder.forEach(name => {
                  const label = defaultLabels.find(l => l.text === name)
                  if (label) {
                    // Format Curve Threshold as dashed black line like curve start/end
                    if (name === 'Curve Threshold') {
                      label.lineDash = [6, 3]
                      label.lineWidth = 2
                      label.pointStyle = 'line'
                      label.strokeStyle = 'black'
                    }
                    pushWithPointStyle(label)
                  }
                })
                
                // Add Curve Start / Curve End if present, with dashed styling
                const curveStart = defaultLabels.find(l => l.text === 'Curve Start')
                if (curveStart) {
                  curveStart.lineDash = [6, 3]
                  curveStart.lineWidth = 2
                  curveStart.pointStyle = 'line'
                  pushWithPointStyle(curveStart)
                }
                const curveEnd = defaultLabels.find(l => l.text === 'Curve End')
                if (curveEnd) {
                  curveEnd.lineDash = [6, 3]
                  curveEnd.lineWidth = 2
                  curveEnd.pointStyle = 'line'
                  pushWithPointStyle(curveEnd)
                }
                
                // Append any remaining labels not in desiredOrder
                defaultLabels.forEach(l => { if (!used.has(l)) pushWithPointStyle(l) })
                
                return ordered
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm'
              },
              parser: function(dateString) {
                return new Date(dateString)
              },
              minUnit: 'hour'
            },
            title: {
              display: true,
              text: 'Time'
            },
            min: this.timelineStart ? new Date(this.timelineStart).getTime() : undefined,
            max: this.timelineEnd ? new Date(this.timelineEnd).getTime() : undefined,
            ticks: {
              callback: (value, index, ticks) => {
                const date = new Date(value)
                const formatted = date.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                })
                return formatted
              }
            },
          },
          y: {
            title: {
              display: true,
              text: 'TAC'
            },
            min: yAxisMin,
            max: yAxisMax
          }
        }
      }
      
      // Cache the options
      this._cachedChartOptions = {
        key: cacheKey,
        options: options
      }
      
      return options
    }
  },
  methods: {
    // Calculate y-axis minimum with default of -5, but allow lower if data requires it
    getYAxisMin() {
      const defaultMin = -5
      
      if (!this.processedChartData || !this.processedChartData.datasets || this.processedChartData.datasets.length === 0) {
        return defaultMin
      }
      
      // Extract all y values from all datasets
      const allYValues = this.processedChartData.datasets
        .flatMap(dataset => dataset.data || [])
        .map(point => point.y)
        .filter(y => y !== null && y !== undefined && !isNaN(y))
      
      if (allYValues.length === 0) {
        return defaultMin
      }
      
      const dataMin = Math.min(...allYValues)
      
      // Use default min of -5, but allow lower if data extends below -5
      return Math.min(defaultMin, dataMin)
    },
    
    // Calculate y-axis maximum with default of 20, but allow higher if data requires it
    getYAxisMax() {
      const defaultMax = 20
      
      if (!this.processedChartData || !this.processedChartData.datasets || this.processedChartData.datasets.length === 0) {
        return defaultMax
      }
      
      // Extract all y values from all datasets
      const allYValues = this.processedChartData.datasets
        .flatMap(dataset => dataset.data || [])
        .map(point => point.y)
        .filter(y => y !== null && y !== undefined && !isNaN(y))
      
      if (allYValues.length === 0) {
        return defaultMax
      }
      
      const dataMax = Math.max(...allYValues)
      
      // Use default max of 20, but allow higher if data extends above 20
      return Math.max(defaultMax, dataMax)
    },
    
    // Process data points for chart datasets
    processDataPoints(data, valueField) {
      const processed = data.map(row => {
        try {
          // Ensure we have a valid datetime
          let xMs
          if (row.tMs != null) {
            xMs = row.tMs
          } else if (row.datetime) {
            const d = new Date(row.datetime)
            xMs = isNaN(d.getTime()) ? null : d.getTime()
            if (xMs === null) {
              console.warn('Invalid datetime in data:', row.datetime)
              return null
            }
          } else {
            console.warn('Missing datetime in data row')
            return null
          }
          
          // Ensure we have a valid value
          let y
          if (Array.isArray(valueField)) {
            for (const key of valueField) {
              if (row[key] !== undefined && row[key] !== null) {
                const parsed = parseFloat(row[key])
                if (!isNaN(parsed)) {
                  y = parsed
                  break
                }
              }
            }
            if (y === undefined) return null
          } else {
            if (valueField && row[valueField] !== undefined && row[valueField] !== null) {
              y = parseFloat(row[valueField])
              if (isNaN(y)) {
                console.warn('Invalid value in data:', row[valueField])
                return null
              }
            } else {
              console.warn('Missing or invalid value field:', valueField)
              return null
            }
          }
          
          // Return a clean object with only the necessary properties
          return { x: xMs, y }
        } catch (error) {
          console.error('Error processing data row:', error, row)
          return null
        }
      }).filter(item => item !== null)
      
      return processed
    },
    // Determine value fields for raw categories (prefer "without imputation" columns when available)
    getRawValueFields() {
      const fallback = ['TAC_pre_imputation']
      if (this.filteredData && this.filteredData.length > 0) {
        const candidateKeys = [
          'TAC_without_imputation',
          'TAC_no_imputation',
          'TAC_raw_without_imputation',
          'TAC_pre_no_imputation'
        ]
        const available = candidateKeys.filter(k => this.filteredData[0][k] !== undefined)
        const chosen = this.localHideRawLowQualityWithImputation && available.length > 0 ? available : fallback
        return chosen
      }
      return fallback
    },
    getImputedValueFields() {
      // Use TAC (final column) for imputed data - matches CLI visualization
      // TAC_pre_savgol is created after imputation, but CLI uses TAC (final) which
      // contains the same imputed values (savgol only smooths non-imputed data)
      return ['TAC']
    },
    
    // Add curve start/end lines to datasets
    addCurveLines(datasets) {
      try {
        // Get the Y range from the data to position the lines
        const allYValues = datasets.flatMap(ds => ds.data.map(point => point.y)).filter(y => !isNaN(y))
        if (allYValues.length === 0) {
          console.log('📊 SignalProcessingChart: No Y values available for curve lines')
          return
        }
        
        const minY = Math.min(...allYValues)
        const maxY = Math.max(...allYValues)
        const yRange = maxY - minY
        const margin = yRange * 0.05 // 5% margin from edges
        

        
        // Use curveTimelineMap if available, otherwise fallback to curveFeatures
        const curvesToProcess = Object.keys(this.curveTimelineMap).length > 0 ? 
          Object.values(this.curveTimelineMap) : 
          this.curveFeatures.map((curve, index) => ({
            id: curve.curve_id || index,
            index: index,
            startDate: new Date(curve.begin_CURVE),
            endDate: new Date(curve.end_CURVE)
          }))
        
        // Determine which curve to show based on selectedCurveIndex
        // Default to curve 0 if no selection or if selectedCurveIndex is invalid
        const curveIndexToShow = this.selectedCurveIndex !== '' && !isNaN(parseInt(this.selectedCurveIndex))
          ? parseInt(this.selectedCurveIndex)
          : 0
        
        // Get the curve to display (from curveTimelineMap if available, otherwise from curveFeatures array)
        let curveToDisplay = null
        if (Object.keys(this.curveTimelineMap).length > 0) {
          // Use curveTimelineMap (indexed by curve index)
          curveToDisplay = this.curveTimelineMap[curveIndexToShow]
        } else if (curvesToProcess.length > curveIndexToShow) {
          // Fallback to array access
          curveToDisplay = curvesToProcess[curveIndexToShow]
        }
        
        console.log('📊 SignalProcessingChart: Using curves for positioning:', {
          hasCurveTimelineMap: Object.keys(this.curveTimelineMap).length > 0,
          selectedCurveIndex: this.selectedCurveIndex,
          curveIndexToShow,
          curveToDisplay: curveToDisplay ? {
            id: curveToDisplay.id,
            index: curveToDisplay.index,
            startDate: curveToDisplay.startDate,
            endDate: curveToDisplay.endDate
          } : null
        })
        
        // Process the selected curve (or curve 0 if none selected)
        if (curveToDisplay && curveToDisplay.startDate && curveToDisplay.endDate) {
          const isSelected = this.selectedCurveIndex !== '' && parseInt(this.selectedCurveIndex) === curveIndexToShow
          
          try {
            const curveStartTime = curveToDisplay.startDate
            const curveEndTime = curveToDisplay.endDate
            
            if (!isNaN(curveStartTime.getTime()) && !isNaN(curveEndTime.getTime())) {
              // Get current timeline range
              const timelineStart = new Date(this.timelineStart)
              const timelineEnd = new Date(this.timelineEnd)
              
              // Only show curve lines if they fall within the current timeline range
              if (curveStartTime <= timelineEnd && curveEndTime >= timelineStart) {
                // Calculate the actual start and end positions within the current timeline
                const displayStartTime = Math.max(curveStartTime.getTime(), timelineStart.getTime())
                const displayEndTime = Math.min(curveEndTime.getTime(), timelineEnd.getTime())
                
                // Curve start line (dark blue) - only if start is within timeline
                if (curveStartTime >= timelineStart) {
                  const curveStartTimeForLine = displayStartTime
                  
                  console.log(`📊 Adding Curve ${curveIndexToShow + 1} Start line at:`, {
                    curveStartTime: curveStartTimeForLine,
                    displayStartTime: displayStartTime,
                    timelineStart: this.timelineStart,
                    timelineEnd: this.timelineEnd
                  })
                  datasets.push({
                    label: 'Curve Start',
                    data: [
                      { x: curveStartTimeForLine, y: minY + margin },
                      { x: curveStartTimeForLine, y: maxY - margin }
                    ],
                    borderColor: 'rgba(0, 0, 139, 1)',
                    backgroundColor: 'rgba(0, 0, 139, 0.1)',
                    borderWidth: isSelected ? 3 : 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointStyle: 'line',
                    showLine: true,
                    tension: 0,
                    spanGaps: true,
                    type: 'line'
                  })
                }
                
                // Curve end line (dark red) - only if end is within timeline
                if (curveEndTime <= timelineEnd) {
                  const curveEndTimeForLine = displayEndTime
                  
                  console.log(`📊 Adding Curve ${curveIndexToShow + 1} End line at:`, {
                    curveEndTime: curveEndTimeForLine,
                    displayEndTime: displayEndTime,
                    timelineStart: this.timelineStart,
                    timelineEnd: this.timelineEnd
                  })
                  datasets.push({
                    label: 'Curve End',
                    data: [
                      { x: curveEndTimeForLine, y: minY + margin },
                      { x: curveEndTimeForLine, y: maxY - margin }
                    ],
                    borderColor: 'rgba(139, 0, 0, 1)',
                    backgroundColor: 'rgba(139, 0, 0, 0.1)',
                    borderWidth: isSelected ? 3 : 2,
                    borderDash: [5, 5],
                    fill: false,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointStyle: 'line',
                    showLine: true,
                    tension: 0,
                    spanGaps: true,
                    type: 'line'
                  })
                }
              }
            }
          } catch (error) {
            console.warn(`Error adding curve lines for curve ${curveIndexToShow + 1}`, error)
          }
        }
      } catch (error) {
        console.error('Error adding curve lines:', error)
      }
    },
    
    // Handle legend click events
    handleLegendClick(e, legendItem, legend) {
      const chart = legend.chart
      
      // Handle grouped curve start/end legend items
      if (legendItem.datasetIndex === 'curve-start' || legendItem.datasetIndex === 'curve-end') {
        const isStart = legendItem.text === 'Curve Start'
        const isEnd = legendItem.text === 'Curve End'
        
        if (isStart || isEnd) {
          // Toggle visibility of all curve start or end datasets
          chart.data.datasets.forEach((dataset, index) => {
            if (dataset.label && dataset.label.includes('Curve')) {
              const isStartDataset = dataset.label.includes('Start')
              const isEndDataset = dataset.label.includes('End')
              
              // Toggle visibility based on which legend item was clicked
              if ((isStart && isStartDataset) || (isEnd && isEndDataset)) {
                const meta = chart.getDatasetMeta(index)
                meta.hidden = !meta.hidden
              }
            }
          })
        }
      } else {
        // Handle regular dataset legend items
        const index = legendItem.datasetIndex
        const ci = chart.getDatasetMeta(index)
        
        if (ci.hidden) {
          ci.hidden = false
        } else {
          ci.hidden = true
        }
      }
      
      chart.update()
    },
    

    
    // Chart event handlers
    onChartReady(chart) {
      this.chartInstance = chart
      this.isChartReady = true
      
      this.$emit('chart-ready', chart)
    },
    
    onChartUpdated(chart) {
      this.$emit('chart-updated', chart)
    },
    
    onChartError(error) {
      console.error('Signal processing chart error:', error)
      this.$emit('chart-error', error)
    },
    
    // Clear caches when needed
    clearCaches() {
      this._cachedChartOptions = null
      this._cachedProcessedData = null
    },
    
    // Public methods for external access
    getChart() {
      return this.$refs.baseChart?.getChart()
    },
    
    isReady() {
      return this.$refs.baseChart?.isReady()
    },
    
    async updateChart(newData = null, newOptions = null) {
      // If no new data provided, use the current processed chart data
      if (!newData) {
        newData = this.processedChartData
        console.log('📊 SignalProcessingChart: Using current processedChartData for update')
      }
      return await this.$refs.baseChart?.updateChart(newData, newOptions)
    },
    
    destroyChart() {
      return this.$refs.baseChart?.destroyChart()
    },
    
    forceStopChart() {
      return this.$refs.baseChart?.forceStopChart()
    },
    
    isChartHealthy() {
      return this.$refs.baseChart?.isChartHealthy()
    },
    
    clearErrorState() {
      return this.$refs.baseChart?.clearErrorState()
    },
    
  },
  watch: {
    // Watch for data changes to clear caches
    filteredData: {
      handler() {
        // Clear caches when data changes
        this.clearCaches()
      },
      deep: true
    },
    // Watch internal checkbox to trigger chart refresh
    localHideRawLowQualityWithImputation: {
      async handler(newVal, oldVal) {
        try {
          this.clearCaches()
          await this.$nextTick()
          // Force a full redraw to ensure membership/value changes render
          await this.destroyChart()
          if (this.$refs.baseChart && this.$refs.baseChart.initializeChart) {
            await this.$refs.baseChart.initializeChart()
          }
        } catch (e) {
          console.warn('Error recreating chart after toggle', e)
        }
      }
    },
    // Watch for timeline changes to clear caches
    timelineStart: {
      handler() {
        console.log('📊 SignalProcessingChart: timelineStart changed, clearing caches')
        this.clearCaches()
      }
    },
    timelineEnd: {
      handler() {
        console.log('📊 SignalProcessingChart: timelineEnd changed, clearing caches')
        this.clearCaches()
      }
    },
    // Watch for checkbox changes to clear caches
    hideRawLowQualityWithImputation: {
      handler() {
        this.clearCaches()
      }
    },
    // Watch for curve selection changes to clear caches
    selectedCurveIndex: {
      handler() {
        this.clearCaches()
      }
    },
    // Watch for curve features changes to clear caches
    curveFeatures: {
      handler() {
        this.clearCaches()
      },
      deep: true
    }
  },
  mounted() {
    // Clear caches when component is mounted
    this.clearCaches()
  },
  beforeUnmount() {
    // Clear caches
    this.clearCaches()
  }
}
</script>

<style scoped>
.signal-processing-chart {
  width: 100%;
  height: 100%;
}

@keyframes dotAnimation {
  0%, 20% {
    content: '';
  }
  40% {
    content: '.';
  }
  60% {
    content: '..';
  }
  80%, 100% {
    content: '...';
  }
}

.loading-dots::after {
  content: '';
  animation: dotAnimation 1.5s steps(4, end) infinite;
}
</style>
